import { useI18n } from '@i18n/I18nProvider'
import { createContext, type KeyboardEvent, type ReactNode, useContext, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'
import { useCloseOnBack } from './useCloseOnBack'
import { useSheetMotion } from './useSheetMotion'

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

// Siguiente elemento al pulsar Tab dentro de un diálogo: al salir por un extremo vuelve al otro
// (el foco no se escapa al mapa de detrás). null → dejar que el navegador mueva el foco.
export const nextTrappedFocus = (
	focusables: readonly HTMLElement[],
	active: Element | null,
	backwards: boolean
): HTMLElement | null => {
	const first = focusables[0]
	const last = focusables[focusables.length - 1]
	if (!first || !last) return null
	const index = active ? focusables.indexOf(active as HTMLElement) : -1
	if (index === -1) return backwards ? last : first
	if (backwards && index === 0) return last
	if (!backwards && index === focusables.length - 1) return first
	return null
}

// Cuántas hojas hay debajo de esta (0: la primera). Las de encima son algo más altas, para que
// se vea que se apilan y no que se sustituyen.
const SheetDepth = createContext(0)
const MAX_HEIGHT = ['max-h-[85dvh]', 'max-h-[91dvh]', 'max-h-[95dvh]'] as const
export const sheetMaxHeight = (depth: number): string =>
	MAX_HEIGHT[Math.min(Math.max(0, Math.trunc(depth) || 0), MAX_HEIGHT.length - 1)] as string

// Asa de una hoja: la raya que indica que se puede arrastrar hacia abajo para cerrarla.
export const SheetHandle = () => (
	<div aria-hidden="true" className="flex justify-center pt-2 pb-1">
		<span className="h-1.5 w-12 rounded-full bg-earth-300" />
	</div>
)

type Props = {
	title: string
	onClose: () => void
	children: ReactNode
}

// Hoja inferior modal (móvil): sube desde abajo, con fondo oscurecido, asa, título y botón de
// cerrar. Se cierra con el botón, con Escape, tocando fuera, arrastrándola hacia abajo (por el asa
// o la cabecera) o con el "atrás" del móvil, y baja al cerrarse. Se pueden apilar: una hoja
// abierta desde otra queda encima, y cada "atrás" cierra la de arriba. Mientras está abierta el
// foco no sale de ella, y al cerrarse vuelve a donde estaba (normalmente, el botón que la abrió).
// Si no cabe, se desplaza. Tapa también la cabecera y la barra inferior.
export const BottomSheet = ({ title, onClose, children }: Props) => {
	const { t } = useI18n()
	const sheet = useRef<HTMLElement>(null)
	const closeButton = useRef<HTMLButtonElement>(null)
	const depth = useContext(SheetDepth)
	const { requestClose, closing, style, dragHandlers } = useSheetMotion(onClose)
	useCloseOnBack(true, requestClose)

	useEffect(() => {
		const previous = document.activeElement as HTMLElement | null
		closeButton.current?.focus()
		return () => previous?.focus?.()
	}, [])

	const onKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			event.stopPropagation()
			requestClose()
			return
		}
		if (event.key !== 'Tab' || !sheet.current) return
		const focusables = [...sheet.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
		const next = nextTrappedFocus(focusables, document.activeElement, event.shiftKey)
		if (next) {
			event.preventDefault()
			next.focus()
		}
	}

	// En un portal a <body>: dentro de un contenedor `fixed` (el mapa) quedaría por debajo de la
	// barra de navegación, por mucho z-index que lleve.
	return createPortal(
		<div className="fixed inset-0 z-50 flex items-end justify-center">
			<button
				type="button"
				tabIndex={-1}
				aria-hidden="true"
				data-testid="sheet-backdrop"
				onClick={requestClose}
				className={`absolute inset-0 size-full animate-fade-in cursor-default bg-black/40 transition-opacity duration-200 motion-reduce:animate-none ${closing ? 'opacity-0' : ''}`}
			/>
			<section
				ref={sheet}
				role="dialog"
				aria-modal="true"
				aria-label={title}
				onKeyDown={onKeyDown}
				data-sheet=""
				data-depth={depth}
				style={style}
				className={`relative flex ${sheetMaxHeight(depth)} w-full max-w-xl animate-sheet-in flex-col rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] shadow-xl motion-reduce:animate-none`}
			>
				{/* La zona que se agarra para bajarla: el asa y la cabecera (sin desplazar la página). */}
				<div {...dragHandlers} data-testid="sheet-grip" className="touch-none select-none">
					<SheetHandle />
					<header className="flex items-center gap-2 border-b border-earth-100 pr-2 pb-2 pl-5">
						<h2 className="flex-1 text-2xl font-semibold">{title}</h2>
						<button
							type="button"
							ref={closeButton}
							onClick={requestClose}
							aria-label={t('common.close')}
							className="flex size-12 items-center justify-center rounded-full text-earth-800"
						>
							<Icon name="close" />
						</button>
					</header>
				</div>
				<div className="overflow-y-auto overscroll-contain p-5">
					<SheetDepth.Provider value={depth + 1}>{children}</SheetDepth.Provider>
				</div>
			</section>
		</div>,
		document.body
	)
}
